import { ChangeEvent, MouseEvent, useEffect, useState } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import { observer } from 'mobx-react';
import { RightSidebar } from '../../../layout/main/rightSidebar';
import { SpotIconType, SpotSvg } from '@enterprise/spot';
import { TaskStatus } from '../../../core/enums/TaskStatus';
import { NavLink, useParams } from 'react-router';
import { TaskEntity } from '../../../core/entity/TaskEntity';
import i18n from 'i18next';
import classNames from 'classnames';
import styles from './ActivityPage.module.scss';
import { useStores } from '../../../hooks';
import { TablePagination } from '@mui/material';

const DEFAULT_PAGE_SIZE = 50;

export const BulkUpdateDetailsPage = observer(function BulkUpdateDetailsPage() {
    const {
        domain: { activityPageStore },
        ui: { router },
    } = useStores();
    const { detailedTask, detailsState = {} } = activityPageStore;
    const { pageSize = DEFAULT_PAGE_SIZE, page = 0 } = detailsState;
    const { id } = useParams<{ id: string }>();
    const [expandedUpdates, setExpandedUpdates] = useState(new Set<number>());

    useEffect(() => {
        if (!id) {
            return;
        }

        void activityPageStore.fetchDetailedTask({ parentTaskId: id, page, pageSize: pageSize });
    }, [id, page, pageSize]);

    useEffect(() => {
        return () => {
            activityPageStore.clearDetailedTask();
        };
    }, []);

    const back = () => {
        router.push('/control-center/activity');
    };

    const runAllFailed = () => {
        if (detailedTask) {
            void activityPageStore.runAllFailedChildTasks(detailedTask.id);
        }
    };

    const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        activityPageStore.updateDetailsState({ page: newPage, pageSize });
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        activityPageStore.updateDetailsState({ page: 0, pageSize: parseInt(event.target.value, 10) });
    };

    const createHeader = () => {
        if (!detailedTask) {
            return <></>;
        }

        return (
            <Container className={styles.bulkUpdateDetailsPageHeader}>
                <Row>
                    <Col xs={9} className="spot-typography__text--body" data-automation-id={'activity-item-bulk-details-header'}>
                        <h4 className={styles.title}>{detailedTask.name}</h4>
                        <p className={classNames('spot-typography__text--tertiary', styles.itemsCount)}>{detailedTask.childrenStats.total} items</p>
                    </Col>
                    {(detailedTask as TaskEntity).hasChildrenToRetry() && (
                        <Col xs={2} className="spot-typography__text--body">
                            <NavLink to={'#'} className={'spot-link'} onClick={runAllFailed}>
                                Run All Failed
                            </NavLink>
                        </Col>
                    )}
                </Row>
            </Container>
        );
    };

    function getTaskStatus(status: TaskStatus, parentStatus?: TaskStatus) {
        if (status === TaskStatus.RUNNING) {
            return { statusText: i18n.t('activityPage:running', 'Running'), className: 'spot-pill--warning' };
        }
        if (status === TaskStatus.SUCCESS) {
            return { statusText: i18n.t('activityPage:success', 'Success'), className: 'spot-pill--positive' };
        }
        if (status === TaskStatus.ERROR) {
            return { statusText: i18n.t('activityPage:error', 'Error'), className: 'spot-pill--negative', isError: true };
        }
        if (status === TaskStatus.CANCEL || parentStatus === TaskStatus.CANCEL) {
            return { statusText: i18n.t('activityPage:canceled', 'Canceled'), className: 'spot-pill--secondary' };
        }
        if (status === TaskStatus.QUEUED) {
            return { statusText: i18n.t('activityPage:queued', 'Queued'), className: 'spot-pill--warning' };
        }
        if (status === TaskStatus.SKIPPED) {
            return { statusText: i18n.t('activityPage:skipped', 'Skipped'), className: 'spot-pill--secondary', hasDescription: true };
        }

        return { statusText: i18n.t('activityPage:pending', 'Pending'), className: 'spot-pill--warning' };
    }

    const createChildDescription = ({ id, status, name, errorMessage }: TaskEntity, parentStatus?: TaskStatus) => {
        const { statusText, className, isError, hasDescription } = getTaskStatus(status, parentStatus);
        const isChildExpanded = expandedUpdates.has(id);
        const hasWarning = Boolean(errorMessage?.find((item) => item?.status === 'WARNING'));
        const iconName = isChildExpanded ? 'caret-up' : 'caret-down';
        const runChild = () => activityPageStore.runTasks([id]);
        const onToggleClick = () => {
            if (isChildExpanded) {
                expandedUpdates.delete(id);
            } else {
                expandedUpdates.add(id);
            }

            setExpandedUpdates(new Set<number>(expandedUpdates));
        };

        return (
            <>
                <Card>
                    <Card.Body>
                        <Container>
                            <Row data-automation-id={'activity-item-details-text'}>
                                <Col xs={6} className="spot-typography__text--body">
                                    <span data-automation-id={'activity-item-bulk-child-name'}>{name}</span>
                                    {Boolean(hasWarning) && (
                                        <p className={styles.warningRow}>
                                            <SpotSvg
                                                icon={SpotIconType.AlertNotification}
                                                className={classNames('spot-icon', styles.warningIcon)}
                                                onClick={onToggleClick}
                                            />
                                            <span className={'spot-typography__text--secondary'}>
                                                Task completed with limitations - Expand for details.
                                            </span>
                                        </p>
                                    )}
                                </Col>
                                <Col xs={3} className="spot-typography__text--body" style={{ textAlign: 'center' }}>
                                    <span
                                        data-automation-id={'activity-item-bulk-child-status'}
                                        className={'spot-pill ' + className + ' status-pill'}
                                    >
                                        {statusText}
                                    </span>
                                </Col>
                                {isError && (
                                    <>
                                        <Col xs={2} className="spot-typography__text--body">
                                            <NavLink to={'#'} className={'spot-link'} onClick={runChild}>
                                                Try Again
                                            </NavLink>
                                        </Col>
                                        <Col xs={1} className="spot-typography__text--body" style={{ textAlign: 'right' }}>
                                            <p>
                                                <SpotSvg
                                                    onClick={onToggleClick}
                                                    icon={iconName}
                                                    className={classNames('spot-link__icon', styles.expandIcon)}
                                                />
                                            </p>
                                        </Col>
                                    </>
                                )}
                                {(hasDescription || hasWarning) && (
                                    <>
                                        <Col xs={{ span: 1, offset: 2 }} className="spot-typography__text--body" style={{ textAlign: 'right' }}>
                                            <p>
                                                <SpotSvg
                                                    onClick={onToggleClick}
                                                    icon={iconName}
                                                    className={classNames('spot-link__icon', styles.expandIcon)}
                                                />
                                            </p>
                                        </Col>
                                    </>
                                )}
                            </Row>
                        </Container>
                    </Card.Body>
                </Card>
                {isChildExpanded && (
                    <Card className={styles.taskErrorMessage}>
                        <Card.Body>
                            <Container>
                                <Row>
                                    <Col xs={12} className={classNames('spot-typography__text--body')}>
                                        {Boolean(errorMessage?.length) &&
                                            errorMessage?.map((item, index) => {
                                                return Object.keys(item).map((key) => {
                                                    if (
                                                        key?.toLowerCase() === 'status' ||
                                                        key?.toLowerCase() === 'provider' ||
                                                        key?.toLowerCase() === 'traceid'
                                                    ) {
                                                        return;
                                                    }

                                                    return (
                                                        <div key={`${key}_${index}`}>
                                                            <span className="spot-typography__font-weight--bold">{key}</span>:{' '}
                                                            {JSON.stringify(item[key])}
                                                        </div>
                                                    );
                                                });
                                            })}
                                        {!errorMessage?.length && 'N/A'}
                                    </Col>
                                </Row>
                            </Container>
                        </Card.Body>
                    </Card>
                )}
            </>
        );
    };

    return (
        <RightSidebar
            isLoading={!detailedTask}
            close={back}
            closeOnClickOutside={true}
            header={createHeader}
            modalBodyStyle={{ margin: '0px', padding: '0px' }}
        >
            <div className={styles.detailsBody}>
                <div className={styles.detailsContent}>
                    {detailedTask?.children?.map((childUpdate, index) => {
                        const key = `${index}_${childUpdate.id}`;
                        return (
                            <div key={key} className="align-items-center justify-content-center">
                                <div>{createChildDescription(childUpdate, detailedTask?.status)}</div>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.paginationFooter}>
                    <TablePagination
                        classes={{ root: styles.paginationFooterRoot }}
                        component="div"
                        count={detailedTask?.childrenStats?.total || 0}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>
            </div>
        </RightSidebar>
    );
});
